// Shared 3-digit visitor-code section — identical format across the staff
// exhibition / zone / exhibit forms. Shows the code + a Regenerate action
// (omitted in create mode / disabled with a note once the entity leaves DRAFT).
// Renders nothing until a code exists.

import type { FC } from "react";

import { Button } from "@shared/components/ui/button";

export interface CodeSectionProps {
  /** The 3-digit code, or null before the entity is first saved. */
  code: string | null;
  /** Heading noun, e.g. "Zone" → "3-Digit Zone Code". */
  label: string;
  /** Sentence noun, e.g. "zone" → "Visitors can use this code to access the zone". */
  entityNoun: string;
  /** Omit to hide the Regenerate button (create mode). */
  onRegenerate?: () => void | Promise<void>;
  /** When set, Regenerate is disabled and the reason is shown. */
  regenerateDisabledReason?: string | null;
}

const CodeSection: FC<CodeSectionProps> = ({
  code,
  label,
  entityNoun,
  onRegenerate,
  regenerateDisabledReason,
}) => {
  if (!code) return null;
  return (
    <div
      className="space-y-4 p-6 bg-purple-50 border border-purple-200 rounded-lg"
      data-testid="code-section"
    >
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-heading font-semibold text-purple-900">
            3-Digit {label} Code
          </h3>
          <p className="text-sm text-purple-700 mt-1">
            Visitors can use this code to access the {entityNoun}
          </p>
        </div>
        {onRegenerate && (
          <Button
            type="button"
            onClick={onRegenerate}
            variant="outline"
            size="sm"
            className="bg-white"
            disabled={Boolean(regenerateDisabledReason)}
            title={regenerateDisabledReason ?? undefined}
            data-testid="regenerate-code-button"
          >
            Regenerate
          </Button>
        )}
      </div>
      <div className="flex items-center gap-4">
        <div className="flex-1 bg-white rounded-lg border-2 border-purple-300 p-6 text-center">
          <div
            className="font-mono text-6xl font-bold text-primary tracking-widest"
            data-testid="code-display"
          >
            {code}
          </div>
        </div>
      </div>
      {regenerateDisabledReason && (
        <p className="text-xs text-purple-700" data-testid="code-note">
          ℹ️ {regenerateDisabledReason}
        </p>
      )}
    </div>
  );
};

export default CodeSection;
